import { DEFAULT_APPROVAL_LIMIT } from '@/config/card'
import { hyperEVM } from '@/config/chain/constants'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type Address } from 'viem'
import { useAccount } from 'wagmi'
import { useCardSolverConfigs } from '@/domain/state'
import { DEFAULT_CARD_CONFIG } from '../constants'
import { createSmartFetch, createSmartGet } from './useAuthenticatedMutation'
import { useCardAuth } from './useCardAuth'
import { useCardAutoSelection } from './useCardAutoSelection'
import { useDelegation } from './useDelegation'
import { useSolverOptions } from './useSolverOptions'

export interface UserCard {
  cardID: string
  chainID: number
  expirationMonth: string
  expirationYear: string
  hookChainID: number
  hookConfig: Record<string, any>
  hookType: string
  label: string | null
  last4: string
  solverConfig: Record<string, any>
  solverCreditProviderAddress: string
  solverType: string
  status:
    | 'active'
    | 'inactive'
    | 'suspended'
    | 'locked'
    | 'frozen'
    | 'ACTIVE'
    | 'INACTIVE'
    | 'SUSPENDED'
    | 'LOCKED'
    | 'FROZEN'
  type: 'virtual' | 'physical' | 'VIRTUAL' | 'PHYSICAL'
}

interface UserCardsResponse {
  data: UserCard[]
}

interface CreateCardRequest {
  cardSolverConfig: Record<string, any>
  chainID: number
  solverType: string
}

interface CreateCardResponse {
  data: UserCard
}

export function useUserCards() {
  const { address } = useAccount()
  const { error: authError, isLoading: authLoading } = useCardAuth()
  const { upsertCardSolverConfig } = useCardSolverConfigs()
  const queryClient = useQueryClient()

  const { hypurrFiBorrowTokens, hypurrFiDebtTokenByBorrowToken } = useSolverOptions()
  const defaultBorrowTokenAddress = hypurrFiBorrowTokens[0]?.address as Address | undefined
  const defaultDebtTokenAddress = defaultBorrowTokenAddress
    ? (hypurrFiDebtTokenByBorrowToken[defaultBorrowTokenAddress.toLowerCase()] as Address | undefined)
    : undefined

  const { needsDelegation, isLoadingAllowance } = useDelegation({
    requiredLimit: DEFAULT_APPROVAL_LIMIT,
    debtTokenAddress: defaultDebtTokenAddress,
    borrowTokenAddress: defaultBorrowTokenAddress,
  })
  const [has409Error, setHas409Error] = useState(false)

  // Query to fetch user cards using createSmartGet for automatic auth
  const getUserCardsUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards`

  const cardsQuery = useQuery<UserCardsResponse>({
    queryKey: ['user-cards', address],
    queryFn: async () => {
      const getUserCards = createSmartGet(getUserCardsUrl)
      const result = await getUserCards()

      return result
    },
    enabled: !!address,
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('Authentication failed') || msg.includes('404')) {
        return false
      }
      return failureCount < 2
    },
  })

  // Auto-create card based on query result
  const cardsData = cardsQuery.data?.data
  const cards = Array.isArray(cardsData) ? [...cardsData].reverse() : []

  const autoCreateAttemptedRef = useRef(false)
  const prevCardCountRef = useRef(0)
  const [isProvisioningCard, setIsProvisioningCard] = useState(false)

  const autoSelection = useCardAutoSelection(!cardsQuery.isLoading && cards.length === 0)

  // Mutation to create a new card using smart mutation with automatic auth
  const createCardUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/create?card_type=virtual`
  const onSuccess = useCallback(
    async (response: CreateCardResponse, request: CreateCardRequest) => {
      setIsProvisioningCard(true)
      if (address && response.data) {
        upsertCardSolverConfig(address, {
          cardID: response.data.cardID,
          solverType: response.data.solverType,
          chainID: response.data.chainID,
          cardSolverConfig: response.data.solverConfig ?? request.cardSolverConfig,
          status: response.data.status,
          label: response.data.label,
          last4: response.data.last4,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['user-cards', address] })
    },
    [address, queryClient, upsertCardSolverConfig],
  )

  const createCardMutation = useMutation<CreateCardResponse, Error, CreateCardRequest>({
    scope: {
      id: 'mutation',
    },
    mutationKey: ['create-card'],
    mutationFn: async (createCardRequest: CreateCardRequest) => {
      const createCardFetch = createSmartFetch(createCardUrl, { method: 'POST' })
      return createCardFetch(createCardRequest)
    },
    onSuccess,
    onError: (error) => {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('USER_ALREADY_HAS_CARD') || msg.includes('MAX_CARDS_REACHED')) {
        setHas409Error(true)
        queryClient.invalidateQueries({ queryKey: ['user-cards', address] })
        return
      }
    },
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('INVALID_TOKEN') || msg.includes('Authentication failed')) {
        return false
      }
      if (msg.includes('USER_ALREADY_HAS_CARD') || msg.includes('MAX_CARDS_REACHED')) {
        return false
      }
      return failureCount < 2
    },
  })

  const autoCreateInFlightRef = useRef(false)

  const autoCreateCard = useCallback(
    (override?: Partial<CreateCardRequest>) => {
      if (!address || createCardMutation.isPending || autoCreateInFlightRef.current) {
        return
      }
      autoCreateInFlightRef.current = true
      autoCreateAttemptedRef.current = true

      const createCardRequest: CreateCardRequest = {
        cardSolverConfig: override?.cardSolverConfig ?? {
          borrowTokenAddress: DEFAULT_CARD_CONFIG.borrowTokenAddress,
        },
        chainID: override?.chainID ?? DEFAULT_CARD_CONFIG.chainId,
        solverType: override?.solverType ?? DEFAULT_CARD_CONFIG.solverType,
      }

      createCardMutation.mutate(createCardRequest, {
        onError: (error) => {
          const msg = error instanceof Error ? error.message : ''
          if (msg.includes('USER_ALREADY_HAS_CARD') || msg.includes('MAX_CARDS_REACHED')) {
            setHas409Error(true)
            createCardMutation.reset()
            cardsQuery.refetch()
            return
          }

          if (
            msg.includes('delegation') ||
            msg.includes('allowance') ||
            msg.includes('insufficient') ||
            msg.includes('approve')
          ) {
            // Don't reset — leave the mutation in error state so the
            // auto-create effect won't retry infinitely.
            return
          }
        },
        onSettled: () => {
          autoCreateInFlightRef.current = false
        },
      })
    },
    [address, createCardMutation, cardsQuery.refetch],
  )

  // Reset flags when address changes (new wallet connected)
  useEffect(() => {
    if (address) {
      autoCreateAttemptedRef.current = false
      autoCreateInFlightRef.current = false
      setHas409Error(false)
      setIsProvisioningCard(false)
    }
  }, [address])

  // Reset auto-create attempt when user deletes all cards so we can retry
  useEffect(() => {
    if (cards.length === 0 && prevCardCountRef.current > 0) {
      autoCreateAttemptedRef.current = false
      setHas409Error(false)
      setIsProvisioningCard(false)
    }
    prevCardCountRef.current = cards.length
  }, [cards.length])

  // Auto-create card effect - only runs when dependencies change
  useEffect(() => {
    // Check if we should auto-create a card
    if (
      address &&
      !authError &&
      !cardsQuery.isLoading &&
      !createCardMutation.isPending &&
      !autoCreateAttemptedRef.current && // Prevent infinite retry on error
      !has409Error && // Don't auto-create if we already got a 409 error
      !autoSelection.isLoading && // Wait for position scoring to finish
      // Case 1: Successful response but empty array
      ((cardsQuery.data && cards.length === 0) ||
        // Case 2: 404 error (no cards found)
        cardsQuery.error?.message?.includes('404'))
    ) {
      if (autoSelection.bestOption && autoSelection.bestCardSolverConfig) {
        autoCreateCard({
          cardSolverConfig: autoSelection.bestCardSolverConfig,
          chainID: hyperEVM.id,
          solverType: autoSelection.bestOption.solverType,
        })
      } else {
        // Fallback: no viable position / best option found — auto-create a
        // pooled HypurrFi USDT borrow card so the user is never left stuck on
        // the "setting up" spinner. They can reconfigure later from the
        // dashboard.
        autoCreateCard()
      }
    }
  }, [
    address,
    authError,
    cardsQuery.isLoading,
    cardsQuery.data,
    cardsQuery.error,
    createCardMutation.isPending,
    cards.length,
    autoCreateCard,
    has409Error,
    autoSelection.isLoading,
    autoSelection.bestOption,
    autoSelection.bestCardSolverConfig,
  ])

  // Manual card creation function with delegation check
  const createCardWithDelegationCheck = async () => {
    if (!address) {
      throw new Error('Wallet not connected')
    }

    const createCardRequest: CreateCardRequest = {
      cardSolverConfig: {
        borrowTokenAddress: DEFAULT_CARD_CONFIG.borrowTokenAddress,
      },
      chainID: DEFAULT_CARD_CONFIG.chainId,
      solverType: DEFAULT_CARD_CONFIG.solverType,
    }

    return createCardMutation.mutateAsync(createCardRequest)
  }

  const activeCard = cards.find(
    (card: UserCard) => card.status.toLowerCase() === 'active' || card.status.toLowerCase() === 'locked',
  )
  const hasActiveCard = !!activeCard

  // Filter out 409 errors (user already has card) from being displayed as errors
  const createCardError =
    createCardMutation.error &&
    !(
      createCardMutation.error.message?.includes('USER_ALREADY_HAS_CARD') ||
      createCardMutation.error.message?.includes('MAX_CARDS_REACHED')
    )
      ? createCardMutation.error
      : null

  useEffect(() => {
    if (cards.length > 0 || has409Error || cardsQuery.error || createCardError) {
      setIsProvisioningCard(false)
    }
  }, [cards.length, has409Error, cardsQuery.error, createCardError])

  return {
    cards,
    activeCard,
    hasActiveCard,
    isLoading: cardsQuery.isLoading || authLoading,
    isCreatingCard: createCardMutation.isPending,
    isProvisioningCard,
    error: cardsQuery.error || createCardError,
    refetch: cardsQuery.refetch,
    createCard: createCardMutation.mutateAsync,
    createCardWithDelegationCheck,
    resetCreateCard: createCardMutation.reset,
    // Delegation state for UI components
    needsDelegation,
    isLoadingAllowance,
  }
}
